import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, ToggleField } from "@yoast/ui-library";
import { defineMessages, FormattedDate, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { provisionedSubscriptionShape } from "./provisionedSubscriptionShape";
import * as styles from "./ProvisioningStyles.module.scss";

const messages = defineMessages( {
	title: {
		id: "provisioning.cancel.title",
		defaultMessage: "Cancel subscription {number}",
	},
	ariaLabel: {
		id: "provisioning.cancel.ariaLabel",
		defaultMessage: "Cancel a provisioned subscription",
	},
	bodyAtPeriodEnd: {
		id: "provisioning.cancel.bodyAtPeriodEnd",
		defaultMessage: "The subscription for {email} will end on {endDate} and will not renew.",
	},
	bodyImmediately: {
		id: "provisioning.cancel.bodyImmediately",
		defaultMessage: "The subscription for {email} ends now.",
	},
	immediatelyLabel: {
		id: "provisioning.cancel.immediatelyLabel",
		defaultMessage: "Cancel immediately",
	},
	immediatelyDescription: {
		id: "provisioning.cancel.immediatelyDescription",
		defaultMessage: "Ends the subscription now instead of at the end of the current period.",
	},
	keep: {
		id: "provisioning.cancel.keep",
		defaultMessage: "Keep subscription",
	},
	confirm: {
		id: "provisioning.cancel.confirm",
		defaultMessage: "Cancel subscription",
	},
	error: {
		id: "provisioning.cancel.error",
		defaultMessage: "Could not cancel the subscription. Please try again.",
	},
	unknownDate: {
		id: "provisioning.cancel.unknownDate",
		defaultMessage: "the end of the current period",
	},
} );

/**
 * Confirms canceling a provisioned subscription, at the end of the period or immediately.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The modal.
 */
function CancelProvisionedSubscriptionModal(
	{
		subscription = null,
		isSubmitting = false,
		hasError = false,
		errorMessage = null,
		onCancel,
		onConfirm,
		intl,
	},
) {
	const [ immediately, setImmediately ] = useState( false );

	// The toggle belongs to one subscription: a later open starts at the safe default again.
	useEffect( () => {
		setImmediately( false );
	}, [ subscription ] );

	const endDate = subscription && subscription.endDate
		? <FormattedDate value={ subscription.endDate } year="numeric" month="short" day="numeric" />
		: <FormattedMessage { ...messages.unknownDate } />;

	return (
		<Modal
			isOpen={ Boolean( subscription ) }
			onClose={ onCancel }
			role="alertdialog"
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel>
				<Modal.Title as="h2" size="2">
					<FormattedMessage
						{ ...messages.title }
						values={ { number: subscription ? subscription.subscriptionNumber : "" } }
					/>
				</Modal.Title>
				<div className={ styles.modalBody }>
					<p>
						<FormattedMessage
							{ ...( immediately ? messages.bodyImmediately : messages.bodyAtPeriodEnd ) }
							values={ {
								email: <strong>{ subscription ? subscription.customerEmail : "" }</strong>,
								endDate: <strong>{ endDate }</strong>,
							} }
						/>
					</p>
					<ToggleField
						id="provisioning-cancel-immediately"
						label={ intl.formatMessage( messages.immediatelyLabel ) }
						description={ intl.formatMessage( messages.immediatelyDescription ) }
						checked={ immediately }
						disabled={ isSubmitting }
						onChange={ setImmediately }
					/>
					{ hasError && (
						<Alert variant="error">
							{ errorMessage || <FormattedMessage { ...messages.error } /> }
						</Alert>
					) }
				</div>
				<div className={ styles.modalActions }>
					<Button variant="secondary" onClick={ onCancel } disabled={ isSubmitting }>
						<FormattedMessage { ...messages.keep } />
					</Button>
					<Button
						variant="error"
						onClick={ () => onConfirm( { immediately } ) }
						isLoading={ isSubmitting }
						disabled={ isSubmitting }
					>
						<FormattedMessage { ...messages.confirm } />
					</Button>
				</div>
			</Modal.Panel>
		</Modal>
	);
}

CancelProvisionedSubscriptionModal.propTypes = {
	subscription: provisionedSubscriptionShape,
	isSubmitting: PropTypes.bool,
	hasError: PropTypes.bool,
	errorMessage: PropTypes.string,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( CancelProvisionedSubscriptionModal );
